/**
 * How fast the tape runs.
 *
 * The original had no speed control at all. Its Playback Controls were "Play,
 * Stop, Rewind, Fast-forward" and the manual is explicit that fast-forward was
 * a *seek* — you watched an on-screen countdown while the tape wound on, and
 * you could not wind it back. This port keeps the four buttons and their names
 * and puts a rate behind them instead, because the constraint that made
 * fast-forward a seek in 1990 (re-simulating faster than real time was the
 * expensive thing) is now the cheap thing: a whole minute of duel re-simulates
 * in about 200 ms.
 *
 * ## The mechanism is borrowed, not rebuilt
 *
 * `flight/time-compression.ts` already solved "run the sim faster than the
 * clock without touching the physics tick": scale `frameSeconds` on the way
 * into `planFixedSteps` and raise the catch-up ceiling to match, leaving
 * `FIXED_DT` alone so AD-007a's fixed-60 Hz rule holds. Playback uses the same
 * mechanism through the same `advance(frameSeconds, inputs, maxSteps)` call —
 * the only difference is that a rate below 1 is meaningful here (the original's
 * Mission Recorder had no slow motion; a modern viewer watching a gunnery pass
 * badly wants it) and that nothing gates it, because there is no fight to be
 * carried past.
 */

/**
 * The ladder, slowest first. `1` must be in it and is the default; the rest are
 * a port choice, chosen so each step is an obvious multiple rather than a dial
 * with no landmarks.
 */
export const PLAYBACK_RATES = [0.25, 0.5, 1, 2, 4, 8] as const;

export type PlaybackRate = (typeof PLAYBACK_RATES)[number];

export const DEFAULT_PLAYBACK_RATE: PlaybackRate = 1;

/** What the bar prints on the rate control. `1x`, `0.5x`, `8x`. */
export function rateLabel(rate: number): string {
  return `${rate}x`;
}

/** The next rate up the ladder, stopping at the top — the original's incremental Fast-forward. */
export function fasterRate(rate: number): PlaybackRate {
  const index = PLAYBACK_RATES.indexOf(rate as PlaybackRate);
  return PLAYBACK_RATES[Math.min(PLAYBACK_RATES.length - 1, index + 1)] ?? DEFAULT_PLAYBACK_RATE;
}

export function slowerRate(rate: number): PlaybackRate {
  const index = PLAYBACK_RATES.indexOf(rate as PlaybackRate);
  return PLAYBACK_RATES[Math.max(0, index - 1)] ?? DEFAULT_PLAYBACK_RATE;
}

/**
 * How far a skip button moves the tape. Ten seconds at 60 Hz — the granularity
 * a person scrubbing for "the bit where the wing came off" actually wants,
 * and, not by accident, the same spacing as the seek index's kept states
 * (`SEEK_INDEX_TICKS`), so a skip back is at most one index gap of re-simulation.
 */
export const SKIP_TICKS = 600;

/**
 * The Location Counter, in the original's own `TIME 00:00:00` format. The third
 * field is hundredths rather than frames: this port's tick is 1/60 s, so a
 * frame count would read 0–59 and invite being mistaken for seconds.
 */
export function locationCounter(tick: number): string {
  const totalHundredths = Math.round((tick / 60) * 100);
  const hours = Math.floor(totalHundredths / 360000);
  const minutes = Math.floor(totalHundredths / 6000) % 60;
  const seconds = Math.floor(totalHundredths / 100) % 60;
  const hundredths = totalHundredths % 100;
  const pad = (value: number) => String(value).padStart(2, "0");
  return hours > 0
    ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}.${pad(hundredths)}`;
}

/** How far through the tape playback is, in `[0, 1]` — the Location Slider's position. */
export function playbackFraction(tick: number, ticks: number): number {
  if (ticks <= 0) return 0;
  return Math.min(1, Math.max(0, tick / ticks));
}

/** The tick a slider position asks for. The inverse of `playbackFraction`. */
export function tickForFraction(fraction: number, ticks: number): number {
  return Math.round(Math.min(1, Math.max(0, fraction)) * ticks);
}
